import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import * as Sentry from "@sentry/react";
import { BotMessageSquare } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants/routes";

interface Props {
  navigate: NavigateFunction;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

interface ErrorDisplayProps {
  footer?: ReactNode;
  title?: string | null;
  message?: string | null;
}

export function ErrorDisplay({
  footer,
  title = "Oops! Something went wrong",
  message = "We apologize for the inconvenience. Our team has been notified and is working to fix the issue.",
}: ErrorDisplayProps) {
  return (
    <div className="min-h-full flex flex-col justify-center items-center p-4">
      <Card className="max-w-md w-full border-none shadow-none">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 bg-accent rounded-full flex items-center justify-center p-4">
            <BotMessageSquare className="w-12 h-12 mx-auto stroke-[1.25]" />
          </div>
          <h2 className="text-2xl font-medium">{title}</h2>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center">{message}</p>
        </CardContent>
        {footer && (
          <CardFooter className="flex justify-center">{footer}</CardFooter>
        )}
      </Card>
    </div>
  );
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo);
    Sentry.captureException(error, {
      extra: {
        errorInfo,
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.navigate(ROUTES.HOME);
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          footer={<Button onClick={this.handleReset}>Go to Home</Button>}
        />
      );
    }

    return this.props.children;
  }
}

// Wrapper component to provide navigation
export function ErrorBoundary({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <ErrorBoundaryClass navigate={navigate}>{children}</ErrorBoundaryClass>
  );
}
